/**
 * ActionConfigBuilder.jsx
 * ========================
 * Main page for configuring and managing actions
 * Supports both standalone mode and popup mode
 * 
 * Created: 2025-11-20
 * Updated: 2025-11-21 - Added new static website actions
 * Features:
 * - Configure actions with trigger elements and events
 * - Save to appName_actions collection in JAYRAM
 * - Load existing actions from dropdown
 * - Live preview with fallback messages
 * - Version control for updates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import CollectionDropdown from '../components/common/CollectionDropdown';
import { getPreviewMessage } from '../utils/actionExecutor';
import { handleReturn, hasReturnUrl } from '../utils/navigationHelper';
import apiService from '../services/apiService';
import { createLogger } from '../utils/logger';

const logger = createLogger('pages/ActionConfigBuilder.jsx');

const ActionConfigBuilder = ({ 
  isPopup = false,           // If true, renders as popup
  onActionSelect = null,      // Callback when action is selected (popup mode)
  onClose = null              // Callback to close popup
}) => {
  const { appName } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State for current action being configured
  const [currentAction, setCurrentAction] = useState({
    actionId: '',
    name: '',
    actionType: '',
    actionConfig: {},
    version: 1
  });

  const [selectedActionId, setSelectedActionId] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [savedActions, setSavedActions] = useState([]);
  const [showPreview, setShowPreview] = useState(true);

  // Action types available
  const actionTypes = [
    { value: 'openPage', label: 'Open a Page' },
    { value: 'scrollToSection', label: 'Scroll to Section' },
    { value: 'showContactForm', label: 'Show Contact Form' },
    { value: 'downloadBrochure', label: 'Download Brochure' },
    { value: 'openWhatsApp', label: 'Open WhatsApp' },
    { value: 'makeCall', label: 'Make Phone Call' },
    { value: 'sendEmail', label: 'Send Email' },
    { value: 'copyToClipboard', label: 'Copy to Clipboard' },
    { value: 'toggleElement', label: 'Show/Hide Element' },
    { value: 'playAnimation', label: 'Play Animation/Lottie' },
    { value: 'showBanner', label: 'Show Banner/Ribbon' },
    { value: 'showSubscribePopup', label: 'Show Subscribe Popup' },
    { value: 'showCouponCard', label: 'Show Coupon/Offer Card' },
    { value: 'showQRPopup', label: 'Show QR Code Popup' }
  ];


  // Load saved actions on mount
  useEffect(() => {
    loadSavedActions();
  }, [appName]);

  const loadSavedActions = async () => {
    if (!appName) return;
    
    try {
      logger.info('loadSavedActions', 'Loading actions', { appName });
      const actions = await apiService.getActions(appName);
      setSavedActions(actions);
      logger.info('loadSavedActions', 'Actions loaded', { count: actions.length });
    } catch (error) {
      logger.error('loadSavedActions', 'Failed to load actions', error);
      console.error('Failed to load actions:', error);
    }
  };

  // Handle loading an existing action
  const handleLoadAction = (actionObject) => {
    if (!actionObject) return;
    
    logger.info('handleLoadAction', 'Loading action', { actionId: actionObject.actionId });
    
    // Ensure openTarget is set for openPage actions (migrate from old format)
    let actionConfig = actionObject.actionConfig || {};
    if (actionObject.actionType === 'openPage') {
      // Migrate from old format (navigationType) to new format (openTarget)
      if (actionConfig.navigationType) {
        actionConfig.openTarget = actionConfig.navigationType === 'internal' ? 'INTERNAL' : 'EXTERNAL';
        if (actionConfig.openTarget === 'INTERNAL' && !actionConfig.openMode) {
          actionConfig.openMode = 'CONTENT';
        }
        if (actionConfig.pageName && !actionConfig.targetPageId) {
          actionConfig.targetPageId = actionConfig.pageName;
        }
      } else if (!actionConfig.openTarget) {
        actionConfig.openTarget = 'INTERNAL';
        actionConfig.openMode = 'CONTENT';
      }
    }
    
    setCurrentAction({
      actionId: actionObject.actionId || '',
      name: actionObject.name || '',
      actionType: actionObject.actionType || '',
      actionConfig: actionConfig,
      version: actionObject.version || 1
    });
    
    setSelectedActionId(actionObject.actionId);
    setIsEditMode(true);
  };

  // Generate unique action ID
  const generateActionId = (name) => {
    const safeName = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const timestamp = Date.now();
    return `${safeName}_${timestamp}`;
  };

  // Handle action type change
  const handleActionTypeChange = (e) => {
    const actionType = e.target.value;
    const defaultConfig = actionType === 'openPage' 
      ? { openTarget: 'INTERNAL', openMode: 'CONTENT' } 
      : {};
    
    setCurrentAction({
      ...currentAction,
      actionType: actionType,
      actionConfig: defaultConfig
    });
  };


  // Update action configuration
  const updateActionConfig = (key, value) => {
    setCurrentAction({
      ...currentAction,
      actionConfig: {
        ...currentAction.actionConfig,
        [key]: value
      }
    });
  };

  // Save action
  const handleSaveAction = async () => {
    if (!currentAction.name.trim()) {
      alert('❌ Please enter an action name');
      return;
    }

    if (!currentAction.actionType) {
      alert('❌ Please select an Action Type');
      return;
    }

    // Validate action-specific requirements
    if (currentAction.actionType === 'openPage') {
      const { openTarget, targetPageId, pageName, url } = currentAction.actionConfig;
      if (openTarget === 'EXTERNAL' && !url) {
        alert('❌ Please enter a website URL');
        return;
      }
      if (openTarget !== 'EXTERNAL' && !targetPageId && !pageName) {
        alert('❌ Please select a page');
        return;
      }
    }

    try {
      let actionId = currentAction.actionId;
      let version = currentAction.version;

      // Check if this is an update to existing action
      if (isEditMode && selectedActionId) {
        const confirmUpdate = window.confirm(
          'This action already exists. Do you want to:\n\n' +
          'OK - Save as new version\n' +
          'Cancel - Keep editing'
        );

        if (!confirmUpdate) return;

        version = version + 1;
        actionId = `${actionId}_v${version}`;
      } else if (!actionId) {
        actionId = generateActionId(currentAction.name);
      }

      const actionData = {
        ...currentAction,
        actionId,
        version,
        createdAt: currentAction.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      logger.info('handleSaveAction', 'Saving action', { actionId, appName });
      
      await apiService.saveAction({
        appName,
        actionId,
        actionData
      });

      // Check if we need to return to a previous page (from Process Builder)
      if (!isPopup && hasReturnUrl(searchParams)) {
        alert(`✅ Action "${currentAction.name}" saved! Returning to Process Builder...`);
        setTimeout(() => {
          handleReturn(navigate, searchParams, '/dashboard');
        }, 500);
      } else {
        alert(`✅ Action "${currentAction.name}" saved successfully!`);
        
        // Reload actions list
        await loadSavedActions();
        
        // Reset form
        handleResetForm();
      }
      
    } catch (error) {
      logger.error('handleSaveAction', 'Failed to save action', error);
      alert('❌ Failed to save action. Check console for details.');
      console.error('Save action error:', error);
    }
  };

  // Reset form
  const handleResetForm = () => {
    setCurrentAction({
      actionId: '',
      name: '',
      actionType: '',
      actionConfig: {},
      version: 1
    });
    setSelectedActionId('');
    setIsEditMode(false);
  };

  // Handle selecting action for component (popup mode)
  const handleSelectForComponent = () => {
    if (!currentAction.actionType) {
      alert('❌ Please configure the action first');
      return;
    }

    // Validate action-specific requirements
    if (currentAction.actionType === 'openPage') {
      const { openTarget, targetPageId, pageName, url } = currentAction.actionConfig;
      if (openTarget === 'EXTERNAL' && !url) {
        alert('❌ Please enter a website URL');
        return;
      }
      if (openTarget !== 'EXTERNAL' && !targetPageId && !pageName) {
        alert('❌ Please select a page');
        return;
      }
    }

    if (onActionSelect) {
      onActionSelect(currentAction);
    }
  };


  // Render action configuration form
  const renderActionConfigForm = () => {
    const { actionType } = currentAction;

    if (!actionType) return null;

    switch (actionType) {
      case 'openPage':
        return (
          <div style={styles.configSection}>
            {/* Section 1: What should open? */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Section 1: What should open?</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="openTarget"
                    value="INTERNAL"
                    checked={(currentAction.actionConfig.openTarget || 'INTERNAL') === 'INTERNAL'}
                    onChange={(e) => {
                      updateActionConfig('openTarget', 'INTERNAL');
                      // Clear external URL when switching to internal
                      updateActionConfig('url', '');
                      updateActionConfig('newTab', false);
                      // Set default openMode for internal pages
                      if (!currentAction.actionConfig.openMode) {
                        updateActionConfig('openMode', 'CONTENT');
                      }
                    }}
                    style={styles.radio}
                  />
                  <span style={{ marginLeft: '8px' }}>A page from this app</span>
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="openTarget"
                    value="EXTERNAL"
                    checked={(currentAction.actionConfig.openTarget || 'INTERNAL') === 'EXTERNAL'}
                    onChange={(e) => {
                      updateActionConfig('openTarget', 'EXTERNAL');
                      // Clear internal page when switching to external
                      updateActionConfig('targetPageId', '');
                      updateActionConfig('pageName', '');
                      updateActionConfig('openMode', '');
                      updateActionConfig('popupConfig', {});
                    }}
                    style={styles.radio}
                  />
                  <span style={{ marginLeft: '8px' }}>Another website</span>
                </label>
              </div>
            </div>

            {/* Section 2: How should it open? (only for internal pages) */}
            {(currentAction.actionConfig.openTarget || 'INTERNAL') !== 'EXTERNAL' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Section 2: How should it open?</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="openMode"
                      value="CONTENT"
                      checked={(currentAction.actionConfig.openMode || 'CONTENT') === 'CONTENT'}
                      onChange={(e) => {
                        updateActionConfig('openMode', 'CONTENT');
                        updateActionConfig('popupConfig', {});
                      }}
                      style={styles.radio}
                    />
                    <span style={{ marginLeft: '8px' }}>Change the main content (default)</span>
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="openMode"
                      value="POPUP"
                      checked={(currentAction.actionConfig.openMode || 'CONTENT') === 'POPUP'}
                      onChange={(e) => {
                        updateActionConfig('openMode', 'POPUP');
                        // Set default popup size if not set
                        if (!currentAction.actionConfig.popupConfig?.size) {
                          updateActionConfig('popupConfig', { size: 'medium' });
                        }
                      }}
                      style={styles.radio}
                    />
                    <span style={{ marginLeft: '8px' }}>Open in a popup</span>
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="openMode"
                      value="FULLSCREEN"
                      checked={(currentAction.actionConfig.openMode || 'CONTENT') === 'FULLSCREEN'}
                      onChange={(e) => {
                        updateActionConfig('openMode', 'FULLSCREEN');
                        updateActionConfig('popupConfig', {});
                      }}
                      style={styles.radio}
                    />
                    <span style={{ marginLeft: '8px' }}>Replace the full screen</span>
                  </label>
                </div>
              </div>
            )}

            {/* Section 3: Choose page / link */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Section 3: {(currentAction.actionConfig.openTarget || 'INTERNAL') === 'EXTERNAL' ? 'Choose link' : 'Choose page'}
              </label>
              
              {/* Internal Page Dropdown */}
              {(currentAction.actionConfig.openTarget || 'INTERNAL') !== 'EXTERNAL' && (
                <CollectionDropdown
                  appName={appName}
                  collectionType="pages"
                  label=""
                  value={currentAction.actionConfig.targetPageId || currentAction.actionConfig.pageName || ''}
                  onChange={(value) => {
                    updateActionConfig('targetPageId', value);
                    // Keep pageName for backward compatibility
                    updateActionConfig('pageName', value);
                  }}
                  labelKey="pageName"
                  valueKey="pageName"
                />
              )}

              {/* External URL Input */}
              {(currentAction.actionConfig.openTarget || 'INTERNAL') === 'EXTERNAL' && (
                <>
                  <input
                    type="text"
                    value={currentAction.actionConfig.url || ''}
                    onChange={(e) => updateActionConfig('url', e.target.value)}
                    style={styles.input}
                    placeholder="https://example.com"
                  />
                  <div style={styles.checkboxGroup}>
                    <input
                      type="checkbox"
                      checked={currentAction.actionConfig.newTab || false}
                      onChange={(e) => updateActionConfig('newTab', e.target.checked)}
                      id="newTab"
                    />
                    <label htmlFor="newTab" style={styles.checkboxLabel}>Open in new tab</label>
                  </div>
                </>
              )}
            </div>

            {/* Optional: Popup size (only if popup selected) */}
            {currentAction.actionConfig.openMode === 'POPUP' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Popup size (optional)</label>
                <select
                  value={currentAction.actionConfig.popupConfig?.size || 'medium'}
                  onChange={(e) => {
                    updateActionConfig('popupConfig', {
                      ...(currentAction.actionConfig.popupConfig || {}),
                      size: e.target.value
                    });
                  }}
                  style={styles.select}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            )}
          </div>
        );

      case 'scrollToSection':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Scroll Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Section ID</label>
              <input
                type="text"
                value={currentAction.actionConfig.sectionId || ''}
                onChange={(e) => updateActionConfig('sectionId', e.target.value)}
                style={styles.input}
                placeholder="e.g., contact-section"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Scroll Behavior</label>
              <select
                value={currentAction.actionConfig.behavior || 'smooth'}
                onChange={(e) => updateActionConfig('behavior', e.target.value)}
                style={styles.select}
              >
                <option value="smooth">Smooth</option>
                <option value="auto">Instant</option>
              </select>
            </div>
          </div>
        );

      case 'showContactForm':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Contact Form Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Form ID</label>
              <input
                type="text"
                value={currentAction.actionConfig.formId || ''}
                onChange={(e) => updateActionConfig('formId', e.target.value)}
                style={styles.input}
                placeholder="e.g., contact-form"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Display Type</label>
              <select
                value={currentAction.actionConfig.displayType || 'modal'}
                onChange={(e) => updateActionConfig('displayType', e.target.value)}
                style={styles.select}
              >
                <option value="modal">Modal/Popup</option>
                <option value="inline">Inline</option>
                <option value="slide">Slide-in</option>
              </select>
            </div>
          </div>
        );

      case 'downloadBrochure':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Download Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>File URL</label>
              <input
                type="text"
                value={currentAction.actionConfig.fileUrl || ''}
                onChange={(e) => updateActionConfig('fileUrl', e.target.value)}
                style={styles.input}
                placeholder="https://example.com/brochure.pdf"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>File Name</label>
              <input
                type="text"
                value={currentAction.actionConfig.fileName || ''}
                onChange={(e) => updateActionConfig('fileName', e.target.value)}
                style={styles.input}
                placeholder="brochure.pdf"
              />
            </div>
          </div>
        );

      case 'openWhatsApp':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>WhatsApp Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number (with country code)</label>
              <input
                type="text"
                value={currentAction.actionConfig.phoneNumber || ''}
                onChange={(e) => updateActionConfig('phoneNumber', e.target.value)}
                style={styles.input}
                placeholder="+1234567890"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Pre-filled Message (Optional)</label>
              <textarea
                value={currentAction.actionConfig.message || ''}
                onChange={(e) => updateActionConfig('message', e.target.value)}
                style={styles.textarea}
                rows="3"
                placeholder="Hello! I'm interested in..."
              />
            </div>
          </div>
        );

      case 'makeCall':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Phone Call Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="text"
                value={currentAction.actionConfig.phoneNumber || ''}
                onChange={(e) => updateActionConfig('phoneNumber', e.target.value)}
                style={styles.input}
                placeholder="+1234567890"
              />
            </div>
          </div>
        );

      case 'sendEmail':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Email Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={currentAction.actionConfig.email || ''}
                onChange={(e) => updateActionConfig('email', e.target.value)}
                style={styles.input}
                placeholder="contact@example.com"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Subject (Optional)</label>
              <input
                type="text"
                value={currentAction.actionConfig.subject || ''}
                onChange={(e) => updateActionConfig('subject', e.target.value)}
                style={styles.input}
                placeholder="Inquiry about..."
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Body (Optional)</label>
              <textarea
                value={currentAction.actionConfig.body || ''}
                onChange={(e) => updateActionConfig('body', e.target.value)}
                style={styles.textarea}
                rows="3"
                placeholder="Hi, I would like to..."
              />
            </div>
          </div>
        );

      case 'copyToClipboard':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Clipboard Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Text to Copy</label>
              <textarea
                value={currentAction.actionConfig.textToCopy || ''}
                onChange={(e) => updateActionConfig('textToCopy', e.target.value)}
                style={styles.textarea}
                rows="3"
                placeholder="Coupon code, referral link, or any text..."
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Success Message</label>
              <input
                type="text"
                value={currentAction.actionConfig.successMessage || 'Copied to clipboard!'}
                onChange={(e) => updateActionConfig('successMessage', e.target.value)}
                style={styles.input}
                placeholder="Copied to clipboard!"
              />
            </div>
          </div>
        );

      case 'toggleElement':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Toggle Element Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Target Element ID</label>
              <input
                type="text"
                value={currentAction.actionConfig.targetElementId || ''}
                onChange={(e) => updateActionConfig('targetElementId', e.target.value)}
                style={styles.input}
                placeholder="e.g., promo-banner, sidebar"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Initial State</label>
              <select
                value={currentAction.actionConfig.initialState || 'visible'}
                onChange={(e) => updateActionConfig('initialState', e.target.value)}
                style={styles.select}
              >
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Animation Type</label>
              <select
                value={currentAction.actionConfig.animationType || 'fade'}
                onChange={(e) => updateActionConfig('animationType', e.target.value)}
                style={styles.select}
              >
                <option value="none">None (Instant)</option>
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
              </select>
            </div>
          </div>
        );

      case 'playAnimation':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Animation Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Animation Type</label>
              <select
                value={currentAction.actionConfig.animationType || 'lottie'}
                onChange={(e) => updateActionConfig('animationType', e.target.value)}
                style={styles.select}
              >
                <option value="lottie">Lottie Animation</option>
                <option value="css">CSS Animation</option>
              </select>
            </div>
            {currentAction.actionConfig.animationType === 'lottie' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Lottie JSON URL</label>
                <input
                  type="text"
                  value={currentAction.actionConfig.lottieUrl || ''}
                  onChange={(e) => updateActionConfig('lottieUrl', e.target.value)}
                  style={styles.input}
                  placeholder="https://example.com/animation.json"
                />
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Target Element ID</label>
              <input
                type="text"
                value={currentAction.actionConfig.targetElementId || ''}
                onChange={(e) => updateActionConfig('targetElementId', e.target.value)}
                style={styles.input}
                placeholder="e.g., hero-animation"
              />
            </div>
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={currentAction.actionConfig.loop || false}
                onChange={(e) => updateActionConfig('loop', e.target.checked)}
                id="animationLoop"
              />
              <label htmlFor="animationLoop" style={styles.checkboxLabel}>Loop Animation</label>
            </div>
          </div>
        );

      case 'showBanner':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Banner/Ribbon Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Banner Text</label>
              <textarea
                value={currentAction.actionConfig.bannerText || ''}
                onChange={(e) => updateActionConfig('bannerText', e.target.value)}
                style={styles.textarea}
                rows="2"
                placeholder="🎉 Special Offer! Get 50% OFF today only!"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Position</label>
              <select
                value={currentAction.actionConfig.position || 'top'}
                onChange={(e) => updateActionConfig('position', e.target.value)}
                style={styles.select}
              >
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
            <div style={styles.gridTwo}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Background Color</label>
                <input
                  type="color"
                  value={currentAction.actionConfig.bgColor || '#3B82F6'}
                  onChange={(e) => updateActionConfig('bgColor', e.target.value)}
                  style={styles.colorInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Text Color</label>
                <input
                  type="color"
                  value={currentAction.actionConfig.textColor || '#FFFFFF'}
                  onChange={(e) => updateActionConfig('textColor', e.target.value)}
                  style={styles.colorInput}
                />
              </div>
            </div>
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={currentAction.actionConfig.dismissible !== false}
                onChange={(e) => updateActionConfig('dismissible', e.target.checked)}
                id="bannerDismissible"
              />
              <label htmlFor="bannerDismissible" style={styles.checkboxLabel}>Allow Close</label>
            </div>
          </div>
        );

      case 'showSubscribePopup':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Subscribe Popup Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Popup Title</label>
              <input
                type="text"
                value={currentAction.actionConfig.title || ''}
                onChange={(e) => updateActionConfig('title', e.target.value)}
                style={styles.input}
                placeholder="Subscribe to Our Newsletter"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={currentAction.actionConfig.description || ''}
                onChange={(e) => updateActionConfig('description', e.target.value)}
                style={styles.textarea}
                rows="2"
                placeholder="Get the latest updates and offers directly to your inbox!"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Button Text</label>
              <input
                type="text"
                value={currentAction.actionConfig.buttonText || 'Subscribe'}
                onChange={(e) => updateActionConfig('buttonText', e.target.value)}
                style={styles.input}
                placeholder="Subscribe"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Form Action URL (Optional)</label>
              <input
                type="text"
                value={currentAction.actionConfig.formAction || ''}
                onChange={(e) => updateActionConfig('formAction', e.target.value)}
                style={styles.input}
                placeholder="https://example.com/subscribe"
              />
            </div>
          </div>
        );

      case 'showCouponCard':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>Coupon/Offer Card Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Coupon Code</label>
              <input
                type="text"
                value={currentAction.actionConfig.couponCode || ''}
                onChange={(e) => updateActionConfig('couponCode', e.target.value)}
                style={styles.input}
                placeholder="SAVE50"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Offer Title</label>
              <input
                type="text"
                value={currentAction.actionConfig.offerTitle || ''}
                onChange={(e) => updateActionConfig('offerTitle', e.target.value)}
                style={styles.input}
                placeholder="50% OFF Limited Time Offer!"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Offer Description</label>
              <textarea
                value={currentAction.actionConfig.offerDescription || ''}
                onChange={(e) => updateActionConfig('offerDescription', e.target.value)}
                style={styles.textarea}
                rows="2"
                placeholder="Valid until December 31st"
              />
            </div>
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={currentAction.actionConfig.autoCopy !== false}
                onChange={(e) => updateActionConfig('autoCopy', e.target.checked)}
                id="couponAutoCopy"
              />
              <label htmlFor="couponAutoCopy" style={styles.checkboxLabel}>Auto-copy code on display</label>
            </div>
          </div>
        );

      case 'showQRPopup':
        return (
          <div style={styles.configSection}>
            <h3 style={styles.configTitle}>QR Code Popup Configuration</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>QR Code Content</label>
              <textarea
                value={currentAction.actionConfig.qrContent || ''}
                onChange={(e) => updateActionConfig('qrContent', e.target.value)}
                style={styles.textarea}
                rows="2"
                placeholder="URL, text, or data to encode in QR"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Popup Title</label>
              <input
                type="text"
                value={currentAction.actionConfig.title || ''}
                onChange={(e) => updateActionConfig('title', e.target.value)}
                style={styles.input}
                placeholder="Scan to Download"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={currentAction.actionConfig.description || ''}
                onChange={(e) => updateActionConfig('description', e.target.value)}
                style={styles.input}
                placeholder="Scan this QR code with your phone"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>QR Size</label>
              <select
                value={currentAction.actionConfig.qrSize || 'medium'}
                onChange={(e) => updateActionConfig('qrSize', e.target.value)}
                style={styles.select}
              >
                <option value="small">Small (128px)</option>
                <option value="medium">Medium (256px)</option>
                <option value="large">Large (512px)</option>
              </select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={isPopup ? styles.popupContainer : styles.pageContainer}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {isPopup ? 'Configure Action' : 'Action Configuration Builder'}
          </h1>
          <p style={styles.subtitle}>
            {isPopup 
              ? 'Configure and select an action for your component' 
              : 'Create and manage reusable actions for your application'}
          </p>
        </div>
        {isPopup && onClose && (
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        )}
      </div>

      <div style={styles.content}>
        {/* Load Existing Action */}
        <div style={styles.section}>
          <CollectionDropdown
            appName={appName}
            collectionType="actions"
            label="Load Existing Action"
            value={selectedActionId}
            onChange={handleLoadAction}
            returnFullObject={true}
            labelKey="name"
            valueKey="actionId"
          />
        </div>

        {/* Action Name */}
        <div style={styles.section}>
          <label style={styles.label}>Action Name *</label>
          <input
            type="text"
            value={currentAction.name}
            onChange={(e) => setCurrentAction({ ...currentAction, name: e.target.value })}
            style={styles.input}
            placeholder="e.g., Navigate to Contact Page"
          />
        </div>

        {/* Action Type Selection */}
        <div style={styles.section}>
          <label style={styles.label}>1. Select Action Type *</label>
          <select
            value={currentAction.actionType}
            onChange={handleActionTypeChange}
            style={styles.select}
          >
            <option value="">-- Choose an action --</option>
            {actionTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Action Configuration */}
        {currentAction.actionType && renderActionConfigForm()}

        {/* Live Preview */}
        {currentAction.actionType && (
          <div style={styles.previewSection}>
            <h3 style={styles.previewTitle}>Action Preview</h3>
            <div style={styles.previewBox}>
              <div style={{ textAlign: 'center', color: '#6B7280' }}>
                <div style={{ fontSize: '24px', marginBottom: '12px' }}>🎯</div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
                  {currentAction.name || 'Unnamed Action'}
                </div>
                <div style={{ fontSize: '13px' }}>
                  {getPreviewMessage(currentAction.actionType, currentAction.actionConfig)}
                </div>
              </div>
            </div>
            <p style={styles.previewNote}>
              💡 Tip: This action will work when used on components (buttons, cards, images, etc.)
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          {isPopup ? (
            <>
              <button onClick={handleSelectForComponent} style={styles.primaryButton}>
                Use This Action
              </button>
              <button onClick={handleResetForm} style={styles.secondaryButton}>
                Reset
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSaveAction} style={styles.primaryButton}>
                Save Action
              </button>
              <button onClick={handleResetForm} style={styles.secondaryButton}>
                Reset
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline styles
const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
    padding: '24px',
  },
  popupContainer: {
    backgroundColor: '#FFFFFF',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: '#6B7280',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  section: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  colorInput: {
    width: '100%',
    height: '40px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '8px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  radio: {
    margin: 0,
    cursor: 'pointer',
  },
  configSection: {
    backgroundColor: '#F3F4F6',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  configTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  formGroup: {
    marginBottom: '16px',
  },
  gridTwo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  previewSection: {
    backgroundColor: '#F9FAFB',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '2px solid #E5E7EB',
  },
  previewTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  previewBox: {
    backgroundColor: '#FFFFFF',
    padding: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
    border: '1px solid #E5E7EB',
  },
  previewNote: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#6B7280',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    borderRadius: '6px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default ActionConfigBuilder;